import { Fragment, memo, useCallback, useMemo, useState } from "react";
import CustomInput from "src/generator/CustomInput/CustomInput";
import InputList from "../../InputList/InputList";
import BloodPressure from "../BloodPressure/BloodPressure";
import { Flex } from "../../../styled";
import useInputListController from "../../hooks/useInputListController";
import { ActionIcon, rem, Text, Tooltip } from "@mantine/core";
import useItemsPerRow from "./useItemsPerRow";
import { chunkArray, getAction } from "./VitalsList.utils";
import { usePostApi, useTheme } from "../../../hooks";
import { useParams } from "react-router-dom";
import { getYachtId } from "../../../utils";
import { useFormContext } from "react-hook-form";
import { addDynamicRow } from "../../../redux/slices/medicalHistorySlice";
import { IconPlus } from "@tabler/icons-react";
import VitalsListRenderer from "./VitalsListRenderer";
import dayjs from "dayjs";
import { getNewsScore, getBloodPressureNewsScore } from "./newsScoring";
import TimeInput from "src/components/shared/TimeInput/TimeInput";

interface Props {
  id: string;
  list: any[];
  required: boolean;
  history?: boolean | undefined;
  dynamicRows: boolean;
  tag: string;
  suffix?: string;
  value: string[];
}

const VitalsList = ({ id, list, tag, suffix }: Props) => {
  const { post } = usePostApi();
  const theme = useTheme();
  const { lbeidOrPartId, lbid } = useParams();
  const { setValue } = useFormContext();
  const defaultList = useMemo(() => {
    if (list.length === 0) {
      return [{ id: "new", value: "" }];
    }
    return list;
  }, [list]);

  const { fields, handleAdd, handleRemove, updateField } =
    useInputListController({
      list: defaultList,
      id,
    });
  const handleRecordAction = (e: any, index: number, subTag?: string) => {
    const currentValue = e.target.value;

    const defaultValue = subTag
      ? list[index]?.value?.[subTag]
      : list[index]?.value;
    if (defaultValue === currentValue || !currentValue) return;

    void post({
      request: "record-history-action",
      body: {
        params: {
          lbid,
          meid: lbeidOrPartId,
          yachtid: getYachtId(),
          action: getAction({
            tag,
            subTag,
            value: currentValue,
            suffix,
            defaultValue,
          }),
        },
      },
    });
  };
  const handleInputChange = (index: number, value: any, id: string) => {
    setValue(id, value);
    const curTime = dayjs().format("HH:mm");
    updateField(index, { time: curTime, date: dayjs().format("DD MMM YYYY") });
  };

  const renderInput = useCallback(
    (index: number, item: any) => {
      let backgroundColor = "transparent";

      if (item && item.time && item.date) {
        if (tag === "med_pressure") {
          const { color } = getBloodPressureNewsScore(item.value.sys ?? "");
          backgroundColor =
            color === "transparent" ? "transparent" : `${color}cc`;
        } else if (item) {
          const { color } = getNewsScore(tag, item.value ?? "");
          backgroundColor =
            color === "transparent" ? "transparent" : `${color}cc`;
        }
      }

      return (
        <Flex
          direction="column"
          align="center"
          style={{
            backgroundColor,
            borderRadius: rem(4),
            padding: rem(4),
            transition: "all 0.2s ease",
          }}>
          <Tooltip label={`${item.date} ${item.time}`}>
            <Flex position="relative">
              <Text
                fz={10}
                style={{
                  whiteSpace: "nowrap",
                  color: theme.colors.grayText[0],
                  opacity: 0.8,
                  fontWeight: 200,
                  height: rem(15),
                }}>
                {item.time}
              </Text>
              {item.time && (
                <TimeInput
                  button
                  value={item.time}
                  onInputChange={v => updateField(index, { time: v }, true)}
                  id={`${id}.${index}.time`}
                />
              )}
            </Flex>
          </Tooltip>

          {tag === "med_pressure" ? (
            <BloodPressure
              uuid={`${id}.${index}.value`}
              value={item.value}
              additionalOnChange={(v, id) =>
                handleInputChange(index, v, id as string)
              }
              additionalOnBlur={(e, s) => handleRecordAction(e, index, s)}
              width={rem(60)}
              hideSuffix
            />
          ) : (
            <CustomInput
              numeric
              id={`${id}.${index}.value`}
              value={item.value}
              additionalOnChange={(v, id) =>
                handleInputChange(index, v, id as string)
              }
              additionalOnBlur={e => handleRecordAction(e, index)}
              w={rem(60)}
            />
          )}
        </Flex>
      );
    },
    [tag, suffix, id, fields],
  );
  if (fields.length == 0) return null;
  return (
    <VitalsListRenderer
      // key={fields.length}
      list={fields}
      renderInput={renderInput}
      tag={tag}
      suffix={suffix}
      handleAdd={handleAdd}
    />
  );
};

export default memo(VitalsList);
